import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { TriangleAlert, EllipsisVertical, InfoIcon } from 'lucide-react'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import EventParticipantStatusItems from './EventParticipantStatusItems'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import CharacterAvatar from 'app/frontend/base/components/CharacterAvatar'
import { Link } from 'react-router-dom'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { imageUrl } from 'app/frontend/base/helpers'
import { cn } from '@/lib/utils'
import { ReactNode } from 'react'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import capitalize from 'lodash/capitalize'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props = {
  className?: string
  entry: EventActivityParticipantEntry
  activityId?: number
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  canEditStatus: boolean
  canAdminEdit: boolean
  trigger?: ReactNode
  inGrid?: boolean
}

let cachedTeamMembers: wowaudit.TeamMembership[] | undefined
let cachedEligibleUnlinkedAlts: wowaudit.TeamMembership[] = []
let cachedEntries: EventActivityParticipantEntry[] | undefined
let cachedAssignedAltCharacterIds = new Set<number>()

const getEligibleUnlinkedAlts = (
  teamMembers?: wowaudit.TeamMembership[],
): wowaudit.TeamMembership[] => {
  if (!teamMembers?.length) return []
  if (teamMembers === cachedTeamMembers) return cachedEligibleUnlinkedAlts

  const mainUserIds = new Set(
    teamMembers
      .filter(
        (teamMembership) =>
          !teamMembership.teamRank.for_alts &&
          typeof teamMembership.characterReference.userId === 'number',
      )
      .map((teamMembership) => teamMembership.characterReference.userId),
  )

  cachedEligibleUnlinkedAlts = teamMembers.filter((teamMembership) => {
    if (!teamMembership.teamRank.for_alts) return false
    const userId = teamMembership.characterReference.userId
    if (typeof userId !== 'number') return true
    return !mainUserIds.has(userId)
  })
  cachedTeamMembers = teamMembers
  return cachedEligibleUnlinkedAlts
}

const getAssignedAltCharacterIds = (
  allEntries: EventActivityParticipantEntry[],
): Set<number> => {
  if (allEntries === cachedEntries) return cachedAssignedAltCharacterIds
  cachedAssignedAltCharacterIds = new Set(
    allEntries
      .map((participantEntry) => participantEntry.participant.altCharacterId)
      .filter(
        (characterId): characterId is number => typeof characterId === 'number',
      ),
  )
  cachedEntries = allEntries
  return cachedAssignedAltCharacterIds
}

const EventParticipantContextMenu = ({
  inGrid,
  className,
  entry,
  activityId,
  selectableStatuses,
  canEditStatus,
  canAdminEdit,
  trigger,
}: Props) => {
  const { team } = useTeam()
  const { allEntries } = useTeamEventParticipantsContext()
  const { updateStatus, updateSelection, removeGuest } =
    useTeamEventUpdateContext()
  const selection = activityId ? entry.selections[activityId] : undefined
  const selectedCharacter = resolveEventParticipantCharacter(entry, activityId)
  const selectedTeamMembership = entry.availableTeamMemberships.find(
    (teamMembership) =>
      teamMembership.characterReference.id === selectedCharacter.id,
  )
  const selectedSpecId =
    selection?.specId ?? entry.specId ?? selectedTeamMembership?.specId
  const currentSelectionSelected = selection?.selected ?? entry.selected
  const classSpecializations = getSpecializationsByClass(
    selectedCharacter.classId,
  )
  const unlinkedAltMemberships = getEligibleUnlinkedAlts(team.members).filter(
    (teamMembership) => {
      const characterId = teamMembership.characterReference.id
      if (characterId === selectedCharacter.id) return false
      const assignedAltCharacterIds = getAssignedAltCharacterIds(allEntries)
      if (
        assignedAltCharacterIds.has(characterId) &&
        characterId !== entry.participant.altCharacterId
      ) {
        return false
      }
      return true
    },
  )
  const selectableOwnMemberships = entry.availableTeamMemberships.filter(
    (teamMembership) =>
      teamMembership.characterReference.id !== selectedCharacter.id,
  )
  const originalCharacter = entry.participant.characterReference
  const originalCharacterSpecializations = getSpecializationsByClass(
    originalCharacter.classId,
  )
  const [originalCharacterDefaultSpecId, originalCharacterDefaultSpec] =
    Object.entries(originalCharacterSpecializations)[0] ?? []
  const canSwitchBackToOriginalCharacter = Boolean(
    entry.participant.altCharacterId &&
      originalCharacter &&
      originalCharacter.id !== selectedCharacter.id &&
      !selectableOwnMemberships.some(
        (teamMembership) =>
          teamMembership.characterReference.id === originalCharacter.id,
      ),
  )

  const addedTypeContent = (
    <div
      className={cn(
        'italic font-normal text-[0.7rem] mt-[-0.2rem]',
        entry.participant.addedAsGuest
          ? 'text-info-foreground'
          : 'text-muted-foreground ',
      )}
    >
      Added {entry.participant.addedAsGuest ? 'manually' : 'automatically'}
    </div>
  )

  if (!canAdminEdit && !canEditStatus) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <Button
            variant='ghost'
            size='icon'
            className={cn('h-6 w-6', className)}
          >
            <EllipsisVertical className='h-4 w-4' />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DropdownMenuLabel>
          {entry.participant.userReference ? (
            <div className='flex items-center gap-2'>
              <img src={imageUrl('bnet-logo-blue.svg')} className='w-8 h-8' />

              <div>
                <Link to={entry.participant.characterReference.path}>
                  {entry.participant.userReference.obfuscatedBattletag}
                </Link>
                {addedTypeContent}
              </div>
            </div>
          ) : entry.participant.characterReference ? (
            <div className='flex items-center gap-2'>
              <CharacterAvatar
                character={entry.participant.characterReference}
                className='h-8 w-8'
              />

              <div>
                <Link
                  to={entry.participant.characterReference.path}
                  className='flex items-center gap-1.5'
                >
                  <CharacterClassIcon
                    characterClass={
                      entry.participant.characterReference.characterClass
                    }
                    size='small'
                  />
                  {entry.participant.characterReference.name}
                </Link>

                {addedTypeContent}
              </div>
            </div>
          ) : null}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {canEditStatus && (!inGrid || !activityId) ? (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <EventStatusIcon status={entry.participant.status} />{' '}
                {capitalize(entry.participant.status)}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <EventParticipantStatusItems
                    currentStatus={entry.participant.status}
                    statuses={selectableStatuses}
                    onSelect={(status) => {
                      updateStatus(entry.participant.id, status)
                    }}
                  />
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {canAdminEdit ? (
          <>
            {entry.participant.userReference ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <CharacterClassIcon
                    characterClass={selectedCharacter.characterClass}
                  />
                  <span>{selectedCharacter.name}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {selectableOwnMemberships.length === 0 &&
                    !canSwitchBackToOriginalCharacter ? (
                      <DropdownMenuLabel>
                        <div className='text-muted-foreground font-normal italic max-w-36'>
                          This user has no alts on this team&apos;s roster
                        </div>
                      </DropdownMenuLabel>
                    ) : null}
                    {canSwitchBackToOriginalCharacter && originalCharacter ? (
                      <DropdownMenuItem
                        onClick={() =>
                          updateSelection(
                            entry.participant.id,
                            {
                              characterId: originalCharacter.id,
                              specId: originalCharacterDefaultSpec
                                ? Number(originalCharacterDefaultSpecId)
                                : null,
                              role: originalCharacterDefaultSpec
                                ? originalCharacterDefaultSpec.default_role
                                : 'melee',
                            },
                            activityId,
                          )
                        }
                      >
                        <div className='flex items-center gap-2 w-full'>
                          <CharacterClassIcon
                            characterClass={originalCharacter.characterClass}
                          />
                          <span>{originalCharacter.name}</span>
                        </div>
                      </DropdownMenuItem>
                    ) : null}
                    {selectableOwnMemberships.map((teamMembership) => {
                      const character = teamMembership.characterReference

                      const altSpecs = getSpecializationsByClass(
                        character.classId,
                      )

                      return (
                        <DropdownMenuItem
                          key={character.id}
                          onClick={() =>
                            updateSelection(
                              entry.participant.id,
                              {
                                characterId: character.id,
                                role: teamMembership.role,
                                specId: teamMembership.specId ?? null,
                              },
                              activityId,
                            )
                          }
                        >
                          <div className='flex items-center gap-2 w-full'>
                            <CharacterRoleIcon role={teamMembership.role} />
                            {teamMembership.specId ? (
                              <BlizzardIcon
                                iconName={altSpecs[teamMembership.specId]?.icon}
                              />
                            ) : (
                              <CharacterClassIcon
                                characterClass={character.characterClass}
                              />
                            )}
                            <span>{character.name}</span>
                          </div>
                        </DropdownMenuItem>
                      )
                    })}
                    {unlinkedAltMemberships.length > 0 ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className='flex items-center gap-2 justify-between'>
                          <span>Unlinked alts</span>
                          <Tooltip>
                            <TooltipTrigger>
                              <InfoIcon className='w-4 h-4 text-muted-foreground' />
                            </TooltipTrigger>
                            <TooltipContent>
                              Alts of which the main is unknown or missing from
                              the event
                            </TooltipContent>
                          </Tooltip>
                        </DropdownMenuLabel>
                        {unlinkedAltMemberships.map((teamMembership) => {
                          const character = teamMembership.characterReference
                          const altSpecs = getSpecializationsByClass(
                            character.classId,
                          )

                          return (
                            <DropdownMenuItem
                              key={`unlinked-alt-${character.id}`}
                              onClick={() =>
                                updateSelection(
                                  entry.participant.id,
                                  {
                                    characterId: character.id,
                                    role: teamMembership.role,
                                    specId: teamMembership.specId ?? null,
                                  },
                                  activityId,
                                )
                              }
                            >
                              <div className='flex items-center gap-2 w-full'>
                                <CharacterRoleIcon role={teamMembership.role} />
                                {teamMembership.specId ? (
                                  <BlizzardIcon
                                    iconName={
                                      altSpecs[teamMembership.specId]?.icon
                                    }
                                  />
                                ) : (
                                  <CharacterClassIcon
                                    characterClass={character.characterClass}
                                  />
                                )}
                                <span>{character.name}</span>
                              </div>
                            </DropdownMenuItem>
                          )
                        })}
                      </>
                    ) : null}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            ) : (
              <DropdownMenuLabel>
                <div className='flex items-center gap-2'>
                  <TriangleAlert className='w-4 h-4 text-warning-foreground' />
                  <span className='text-muted-foreground font-normal italic'>
                    Not a wowaudit user
                  </span>
                </div>
              </DropdownMenuLabel>
            )}

            <DropdownMenuSeparator />

            {Object.entries(classSpecializations).map(([nextSpecId, spec]) => (
              <DropdownMenuItem
                key={nextSpecId}
                onClick={() =>
                  updateSelection(
                    entry.participant.id,
                    {
                      role: spec.default_role,
                      specId:
                        selectedSpecId !== Number(nextSpecId)
                          ? Number(nextSpecId)
                          : null,
                      selected: activityId
                        ? !entry.selected ||
                          selectedSpecId !== Number(nextSpecId)
                        : undefined,
                    },
                    activityId,
                  )
                }
              >
                <div
                  className={`flex items-center gap-2 w-full ${
                    selectedSpecId === Number(nextSpecId) ? 'text-primary' : ''
                  }`}
                >
                  <CharacterRoleIcon role={spec.default_role} />
                  <BlizzardIcon iconName={spec.icon} />
                  <span>{spec.name}</span>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        ) : null}

        {canAdminEdit &&
        !['absent', 'missing'].includes(entry.participant.status) ? (
          <DropdownMenuItem
            onClick={() =>
              updateSelection(
                entry.participant.id,
                { selected: !currentSelectionSelected },
                activityId,
              )
            }
          >
            {activityId
              ? currentSelectionSelected
                ? 'Move to benched'
                : 'Move to selected'
              : currentSelectionSelected
              ? 'Bench for all encounters'
              : 'Select for all encounters'}
          </DropdownMenuItem>
        ) : null}

        {canAdminEdit &&
        entry.participant.addedAsGuest &&
        (!inGrid || !activityId) ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className='text-destructive-foreground'
              onClick={() => removeGuest(entry.participant.id)}
            >
              Remove participant
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EventParticipantContextMenu
